import * as React from 'react';
import { ChangeEvent, Component } from 'react';
import {
  DispatchToPropsForCoursesListContainer,
  StateToPropsForCoursesListContainer,
} from '../Containers/CoursesListContainer';
import { H1 } from '../../../__core/Styles/Headings/Headings';
import {
  CoursesListHeadingsGroupStyle,
  CoursesListHeadingStyle,
  CoursesListStyle,
  SearchInput,
  SearchInputIcon,
  SearchInputIconMobile,
  SearchInputMobile,
} from '../Styles/Styles';
import AuthComponent from '../../../__core/Providers/Permissions/AuthComponent';
import ROLES from '../../../__core/Enums/Roles';
import background from '../../../Assets/images/background.svg';
import COURSES_LIST_TABS from '../../../__core/Enums/CoursesListTabs';
import SearchPortalProvider from './SearchPortalProvider';
import { searchIcon } from '../../../Assets/images/search';
import { WithTranslation, withTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import Pagination from '../../../__core/Components/Pagination/Pagination';
import LoaderProvider from '../../../__core/Providers/Loader/LoaderProvider';
import CourseItem from './CourseItem';
import ProductItem from './ProductItem';
import NoItems from './NoItems';
import { setWelcomePageRedirect } from '../Actions/Actions';
import SearchPortalMobileProvider from './SearchPortalMobileProvider';
import REDIRECT_TYPES from '../../../__core/Enums/RedirectTypes';

export const START_PAGE_NUMBER = 1;

type CoursesListState = { activeTab: COURSES_LIST_TABS; filter: string };
type CoursesListProps = WithTranslation &
  StateToPropsForCoursesListContainer &
  DispatchToPropsForCoursesListContainer;

class CoursesList extends Component<CoursesListProps, CoursesListState> {
  state = {
    activeTab: COURSES_LIST_TABS.YOUR_COURSES,
    filter: '',
  };

  componentDidMount(): void {
    const { actions } = this.props;
    actions.fetchCoursesList({ page: START_PAGE_NUMBER });
  }

  setActiveTab = (tab: COURSES_LIST_TABS): void => {
    const { actions } = this.props;

    switch (tab) {
      case COURSES_LIST_TABS.DIRECT_ACCESS:
        actions.fetchProductsList();
        break;
      case COURSES_LIST_TABS.YOUR_COURSES:
        actions.fetchCoursesList({ page: START_PAGE_NUMBER });
        break;
    }

    this.setState({ activeTab: tab });
  };

  goToPage = (pageNumber: number): void => {
    const { actions } = this.props;
    const { filter } = this.state;
    const params: { page: number; signature?: string } = { page: pageNumber };

    if (filter) {
      params.signature = filter;
    }

    actions.fetchCoursesList(params);
  };

  searchCourseInstances = debounce(() => this.goToPage(START_PAGE_NUMBER), 500);

  onChangeSearch = (event: ChangeEvent<HTMLInputElement>): void => {
    const {
      target: { value },
    } = event;

    this.setState({ filter: value }, this.searchCourseInstances);
  };

  onChangeWelcomePageRedirect = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    const { userId } = this.props;
    const {
      target: { checked = false },
    } = event;

    setWelcomePageRedirect(userId, checked);
  };

  render(): JSX.Element {
    const {
      t,
      isLoading,
      courses,
      products,
      coursesListPages,
      coursesListCurrentPage,
      userRedirect,
    } = this.props;

    const { activeTab } = this.state;

    const style = {
      backgroundImage: `url(${background})`,
      backgroundSize: 'cover',
      backgroundPositionY: 'center',
      backgroundPositionX: 'center',
      backgroundAttachment: 'fixed',
      minHeight: 'calc(100vh - 62px)',
    };

    const directAccessPermissions = [
      ROLES.ROLE_MENTOR,
      ROLES.ROLE_LECTURER,
      ROLES.ROLE_SUPER_ADMIN,
    ];

    const searchPermissions = [
      ROLES.ROLE_MENTOR,
      ROLES.ROLE_LECTURER,
      ROLES.ROLE_SUPER_ADMIN,
    ];

    return (
      <div style={style}>
        <AuthComponent componentPermissions={searchPermissions}>
          <React.Fragment>
            <SearchPortalProvider>
              {activeTab === COURSES_LIST_TABS.YOUR_COURSES && (
                <React.Fragment>
                  <form
                    autoComplete="off"
                    onSubmit={(event) => event.preventDefault()}
                  >
                    <SearchInput
                      autoCapitalize="off"
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck="false"
                      id="signature-filter"
                      name="signature-filter"
                      onChange={this.onChangeSearch}
                      placeholder={t('Find by signature')}
                    />
                  </form>
                  <SearchInputIcon>{searchIcon}</SearchInputIcon>
                </React.Fragment>
              )}
            </SearchPortalProvider>
            <SearchPortalMobileProvider>
              {activeTab === COURSES_LIST_TABS.YOUR_COURSES && (
                <React.Fragment>
                  <SearchInputMobile
                    name="signature-filter"
                    onChange={this.onChangeSearch}
                    placeholder={t('Find by signature')}
                  />
                  <SearchInputIconMobile>{searchIcon}</SearchInputIconMobile>
                </React.Fragment>
              )}
            </SearchPortalMobileProvider>
          </React.Fragment>
        </AuthComponent>

        <CoursesListStyle>
          <CoursesListHeadingStyle>
            <CoursesListHeadingsGroupStyle>
              <H1
                id={COURSES_LIST_TABS.YOUR_COURSES}
                onClick={() =>
                  this.setActiveTab(COURSES_LIST_TABS.YOUR_COURSES)
                }
                active={activeTab === COURSES_LIST_TABS.YOUR_COURSES}
              >
                {t('Your courses')}
              </H1>
              <AuthComponent componentPermissions={directAccessPermissions}>
                <H1
                  id={COURSES_LIST_TABS.DIRECT_ACCESS}
                  onClick={() =>
                    this.setActiveTab(COURSES_LIST_TABS.DIRECT_ACCESS)
                  }
                  active={activeTab === COURSES_LIST_TABS.DIRECT_ACCESS}
                >
                  {t('Direct Access')}
                </H1>
              </AuthComponent>
            </CoursesListHeadingsGroupStyle>
            <div className="redirect-input">
              <input
                id="ignore_this_page"
                name="ignore_this_page"
                type="checkbox"
                defaultChecked={userRedirect.type === REDIRECT_TYPES.MATERIAL}
                onChange={this.onChangeWelcomePageRedirect}
              />
              <label htmlFor="ignore_this_page">
                {t("Don't show this screen after logging in")}
              </label>
            </div>
          </CoursesListHeadingStyle>
          <LoaderProvider isLoading={isLoading}>
            <div>
              {activeTab === COURSES_LIST_TABS.DIRECT_ACCESS && (
                <div>
                  {products.map((product) => (
                    <ProductItem key={product.id} product={product} />
                  ))}
                  {!products.length && <NoItems />}
                </div>
              )}

              {activeTab === COURSES_LIST_TABS.YOUR_COURSES && (
                <div>
                  {courses.map((course) => (
                    <CourseItem key={course.id} course={course} />
                  ))}
                  {coursesListPages > 1 && (
                    <Pagination
                      onPageChange={this.goToPage}
                      currentPage={coursesListCurrentPage}
                      totalPage={coursesListPages}
                    />
                  )}
                  {!courses.length && <NoItems />}
                </div>
              )}
            </div>
          </LoaderProvider>
        </CoursesListStyle>
      </div>
    );
  }
}

export default withTranslation()(CoursesList);
